import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link2, Copy, Check, Trash2, Globe, Loader2 } from "lucide-react";

export const ShareLinkManager = ({ itemId }) => {
  const [shareCode, setShareCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = shareCode ? `${window.location.origin}/shared/${shareCode}` : null;

  const createShareLink = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/items/${itemId}/share`);
      setShareCode(res.data.share_code);
      toast.success("Share link created!");
    } catch (error) {
      toast.error("Failed to create share link");
    } finally {
      setLoading(false);
    }
  };

  const removeShareLink = async () => {
    setLoading(true);
    try {
      await axios.delete(`${API}/items/${itemId}/share`);
      setShareCode(null);
      toast.success("Share link removed");
    } catch (error) {
      toast.error("Failed to remove share link");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // Fallback
      const textArea = document.createElement("textarea");
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (shareCode) {
    return (
      <div className="p-4 bg-secondary/10 rounded-xl space-y-3">
        <div className="flex items-center gap-2 text-secondary">
          <Globe className="w-4 h-4" />
          <span className="text-sm font-medium">Public link active</span>
        </div>
        
        <div className="flex gap-2">
          <Input
            value={shareUrl}
            readOnly
            className="bg-card border-charcoal/10 text-sm"
          />
          <Button
            variant="outline"
            size="icon"
            onClick={copyLink}
            className="flex-shrink-0 border-charcoal/10"
            data-testid="copy-share-link-btn"
          >
            {copied ? <Check className="w-4 h-4 text-secondary" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={removeShareLink}
          disabled={loading}
          className="text-destructive hover:text-destructive hover:bg-destructive/10 w-full"
          data-testid="remove-share-link-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
          Remove public link
        </Button>
      </div>
    );
  }

  return (
    <Button
      variant="outline"
      onClick={createShareLink}
      disabled={loading}
      className="w-full rounded-xl border-charcoal/10 h-11"
      data-testid="create-share-link-btn"
    >
      {loading ? (
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
      ) : (
        <Link2 className="w-4 h-4 mr-2" />
      )}
      Create Public Share Link
    </Button>
  );
};

export default ShareLinkManager;
